import React, { useEffect, useRef } from "react";
import { LanderTemplate } from "../commons/constants/Templates";
import { DomainStatus, EventType, ErrorCode, System } from "../commons/Constants";
import { sendErrorEvent } from "../commons/LanderEvent";
import SimpleLayout from "./cashparking/SimpleLayout";
import HegLayout from "./parkweb/HegLayout";
import ParkWebLayout from "./parkweb/ParkWebLayout";
import { YahooLayout } from "./yahoo";
import { useLanderConfig } from "../commons/ConfigContext";
import { InactiveLander } from "./InactiveLander";
import { Redirect } from "./Redirect";
import TargetedLander from "./TargetedLander";
import { AdProvider, useAdProvider } from "./useAdProvider";
import postEvent, { createBaseLanderEvent } from "../commons/LanderEvent";
import { getVisitorIdentifier } from "../commons/HelperFunctions";
import { Settings } from "../config";
import { FeedType, LanderConfig } from "../types/LanderConfig";

/**
 * Appends the visitor id (the `pvisitor` cookie, the same value stamped as
 * `visitorId` on events) to the intent-routing redirect URL as `referrer_id`
 * (PARKING-5190). The downstream carries referrer_id into Salesforce
 * (Lead field Referrer_AA__c), so this frontend stamp ties a routed visit to
 * the resulting lead.
 *
 * Uses the URL API so pre-existing utm/traffic params are preserved and no
 * duplicate keys/trailing `&` are introduced. If `to` is not a valid absolute
 * URL, the original value is returned unchanged so a malformed URL can never
 * break the redirect.
 */
function withReferrerId(to: string): string {
  const visitorId = getVisitorIdentifier();
  if (!visitorId) return to;
  try {
    const url = new URL(to);
    url.searchParams.set("referrer_id", visitorId);
    return url.toString();
  } catch {
    return to;
  }
}

/**
 * Emits exactly one INTENT_REDIRECT exposure event (PARKING-5158) for the
 * intent-routing treatment arm, then hands off to <Redirect>.
 *
 * The exposure emitter is rendered BEFORE <Redirect> so its mount effect runs
 * first (React fires sibling passive effects in tree order). The send uses the
 * shared postEvent path with keepalive:true, so the request survives the
 * immediate window.location.replace() in Redirect's effect while preserving
 * proxy routing + abpParams.
 */
function IntentExposureEvent({ landerConfig }: { landerConfig: LanderConfig }) {
  const emittedRef = useRef(false);
  useEffect(() => {
    if (emittedRef.current) return;
    emittedRef.current = true;
    const event = createBaseLanderEvent(
      EventType.INTENT_REDIRECT,
      landerConfig
    );
    postEvent(Settings.EVENT_PUBLISH_API, event, { keepalive: true });
  }, [landerConfig]);
  return null;
}

function IntentRoutingRedirect({
  to,
  landerConfig,
}: {
  to: string;
  landerConfig: LanderConfig;
}) {
  return (
    <>
      <IntentExposureEvent landerConfig={ landerConfig } />
      <Redirect to={ withReferrerId(to) } replace />
    </>
  );
}

export default function LanderRouter() {
  const { landerConfig, queryConfig, targeted } = useLanderConfig();
  const { provider, rsocUrl, rsocError } = useAdProvider();
  const isYahooLayout =
    landerConfig.system !== System.PARKWEB &&
    landerConfig.lander?.feedID === FeedType.YAHOO;
  const errorFiredRef = useRef(false);

  useEffect(() => {
    if (rsocError && !errorFiredRef.current) {
      errorFiredRef.current = true;
      sendErrorEvent(landerConfig, ErrorCode.ERROR_RSOC_REDIRECT);
    }
  }, [rsocError, landerConfig]);

  // 1. Targeted (FOS iframe)
  if (targeted) {
    return <TargetedLander targeted={ targeted } queryConfig={ queryConfig } />;
  }

  // 2. Inactive domain - fires ERROR event
  // Missing status is treated as inactive (API should always include status for valid domains)
  if (landerConfig.domain?.status?.internal !== DomainStatus.ACTIVE) {
    return <InactiveLander />;
  }

  // 3. TurnKey Reseller redirect
  const turnKeyUrl = landerConfig.turnKeyReseller?.redirectUrl?.trim();
  if (turnKeyUrl?.length) {
    return <Redirect to={ turnKeyUrl } replace />;
  }

  // 4. RSOC redirect
  if (provider === AdProvider.RSOC && rsocUrl) {
    return <Redirect to={ rsocUrl } />;
  }

  // 5. Intent-routing redirect (PARKING-5157)
  // Inert until the backend supplies intentRouting.redirectUrl on an
  // otherwise-parked render.
  const intentRoutingUrl = landerConfig.intentRouting?.redirectUrl?.trim();
  if (intentRoutingUrl?.length) {
    return (
      <IntentRoutingRedirect
        to={ intentRoutingUrl }
        landerConfig={ landerConfig }
      />
    );
  }

  // 6. Template-based layout
  switch (landerConfig.lander.template) {
    case LanderTemplate.PARK_WEB:
      return <ParkWebLayout />;
    case LanderTemplate.PARK_WEB_HEG:
      return <HegLayout />;
    default:
      return isYahooLayout ? <YahooLayout /> : <SimpleLayout />;
  }
}
